import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Download, 
  Calendar,
  ArrowRight,
  Tag,
  CheckCircle2,
  Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resources() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clForm, setClForm] = useState({ first_name: "", email: "", client_type: "", consent: false });
  const [clSubmitting, setClSubmitting] = useState(false);
  const [clDone, setClDone] = useState(false);

  const submitChecklist = async (e) => {
    e.preventDefault();
    if (!clForm.first_name || !clForm.email) {
      toast.error("Please enter your first name and email.");
      return;
    }
    if (!clForm.consent) {
      toast.error("Please agree to receive the checklist and updates.");
      return;
    }
    setClSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        form_type: "checklist",
        name: clForm.first_name,
        email: clForm.email,
        service_category: "Checklist Download",
        source_page: "/resources",
        consent: clForm.consent,
        payload: { client_type: clForm.client_type },
      });
      setClDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setClSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/posts`);
        setPosts(res.data);
      } catch (e) {
        console.log("Using placeholder posts");
        setPosts([
          {
            id: "1",
            title: "5 Tax Deductions Self-Employed Workers Often Miss",
            slug: "tax-deductions-self-employed",
            excerpt: "Are you leaving money on the table? Here are five commonly overlooked deductions for freelancers and 1099 workers.",
            category: "Tax Tips",
            created_at: new Date().toISOString()
          },
          {
            id: "2",
            title: "When Should You Form an LLC or S-Corp?",
            slug: "llc-vs-scorp-decision",
            excerpt: "Understanding the right business structure can save you thousands in taxes. Here's what you need to know.",
            category: "Business",
            created_at: new Date().toISOString()
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const downloadables = [
    {
      title: "Tax Prep Document Checklist",
      description: "A comprehensive list of documents you'll need for your tax return. Print it out and check off items as you gather them.",
      icon: FileText,
      action: "/tax-prep",
      actionText: "View & Print Checklist"
    },
    {
      title: "Self-Employed Expense Tracker",
      description: "A simple spreadsheet template to track your business expenses throughout the year. Stay organized and tax-ready.",
      icon: Download,
      action: "#",
      actionText: "Coming Soon"
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Resources
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Tax Tips, Guides & Tools
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300">
              Helpful resources to keep you informed and organized throughout the year.
            </p>
          </div>
        </div>
      </section>

      {/* Lead Magnet: Tax-Ready Checklist */}
      <section className="bg-white py-16 md:py-20 border-b border-gray-100" data-testid="lead-magnet-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-navy text-white noise-overlay rounded-3xl p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-bronze/20 text-bronze rounded-full px-4 py-1.5 mb-4 text-sm font-medium">
                <Gift className="w-4 h-4" /> Free Download
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Get the Tax-Ready Checklist</h2>
              <p className="font-body text-gray-300">
                Know exactly what to gather before tax season. Enter your details and we'll send you our free checklist.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8">
              {clDone ? (
                <div className="text-center py-6" data-testid="checklist-success">
                  <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-navy mb-2">Check your inbox!</h3>
                  <p className="font-body text-gray-600 mb-4">Your Tax-Ready Checklist is on its way to {clForm.email}.</p>
                  <Button asChild className="bg-navy text-white rounded-full">
                    <Link to="/tax-prep">View the Checklist Now</Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={submitChecklist} className="space-y-4">
                  <div>
                    <Label htmlFor="cl-name" className="text-navy">First Name *</Label>
                    <Input id="cl-name" data-testid="checklist-name" value={clForm.first_name} onChange={(e) => setClForm((f) => ({ ...f, first_name: e.target.value }))} placeholder="Jane" />
                  </div>
                  <div>
                    <Label htmlFor="cl-email" className="text-navy">Email *</Label>
                    <Input id="cl-email" type="email" data-testid="checklist-email" value={clForm.email} onChange={(e) => setClForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@email.com" />
                  </div>
                  <div>
                    <Label className="text-navy">I am a...</Label>
                    <div className="flex gap-3 mt-1">
                      {["Individual", "Business owner"].map((t) => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setClForm((f) => ({ ...f, client_type: t }))}
                          className={`flex-1 rounded-full py-2 text-sm font-medium border transition-colors ${clForm.client_type === t ? "bg-bronze text-navy border-bronze" : "border-gray-300 text-gray-600 hover:border-bronze"}`}
                          data-testid={`checklist-type-${t.toLowerCase().replace(' ', '-')}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={clForm.consent} onCheckedChange={(v) => setClForm((f) => ({ ...f, consent: !!v }))} data-testid="checklist-consent" className="mt-1" />
                    <span className="font-body text-xs text-gray-600">I agree to receive the checklist and occasional tax tips. No sensitive information required.</span>
                  </label>
                  <Button type="submit" disabled={clSubmitting} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium" data-testid="checklist-submit">
                    {clSubmitting ? "Sending..." : "Send Me the Checklist"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Blog</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Latest Articles
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="bg-white border-0 shadow-sm animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link 
                  key={post.id} 
                  to={`/resources/${post.slug}`}
                  data-testid={`blog-post-${index}`}
                >
                  <Card className="bg-white border-0 shadow-sm hover-lift h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Tag className="w-4 h-4 text-bronze" />
                        <span className="font-mono text-xs text-bronze uppercase">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-navy mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="font-body text-gray-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.created_at)}
                        </div>
                        <span className="text-bronze font-medium text-sm flex items-center gap-1">
                          Read <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-navy mb-2">No Articles Yet</h3>
                <p className="font-body text-gray-600">
                  Check back soon for helpful tax tips and guides.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Downloadables */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Downloads</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Helpful Tools
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {downloadables.map((item, index) => (
              <Card 
                key={item.title} 
                className="bg-bone border-0"
                data-testid={`downloadable-${index}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-7 h-7 text-bronze" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-navy mb-2">
                        {item.title}
                      </h3>
                      <p className="font-body text-gray-600 mb-4">
                        {item.description}
                      </p>
                      <Button
                        asChild
                        variant={item.action === "#" ? "outline" : "default"}
                        className={`rounded-full font-medium ${
                          item.action === "#" 
                            ? "border-gray-300 text-gray-500 cursor-not-allowed" 
                            : "bg-navy hover:bg-gray-800 text-white"
                        }`}
                        disabled={item.action === "#"}
                      >
                        {item.action === "#" ? (
                          <span>{item.actionText}</span>
                        ) : (
                          <Link to={item.action}>
                            {item.actionText}
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Link>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Have a Tax Question?
          </h2>
          <p className="font-body text-gray-300 mb-8">
            Our team is here to help. Reach out for personalized guidance.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="resources-cta-btn"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
